/**
 * Block splitting for streamed markdown.
 *
 * Lives outside `MessageContent.tsx` so these pure helpers can be unit-tested
 * (and so exporting them doesn't break fast refresh for the component file).
 */
import { marked } from "marked";

/**
 * True when the tail of a streaming block is a markdown link whose closing
 * `)` hasn't arrived yet — i.e. `[label](https://…` with no matching close.
 *
 * Why this matters: a presigned S3 download URL streams in over many tokens,
 * and an unterminated `[label](url` is not yet link syntax, so the renderer
 * paints the half-arrived URL as raw text. The user watches a wall of
 * `X-Amz-Signature` query junk reflow on every token, then vanish when the
 * `)` finally lands. Holding the block back renders the link exactly once.
 *
 * Scanning from the end (rather than counting brackets across the block)
 * keeps this O(tail) and confines it to the only case that matters: an open
 * link at the very end of the in-progress block. A closed link earlier in the
 * block, or a stray `(`, both fall through without a match.
 */
function hasUnclosedTrailingLink(block: string): boolean {
  const linkUrlStart = block.lastIndexOf("](");
  if (linkUrlStart === -1) {
    return false;
  }
  // A `)` after `](` means that link already closed; whatever follows is
  // ordinary text, so nothing is pending.
  if (block.includes(")", linkUrlStart + 2)) {
    return false;
  }
  // Require the `[` that opens the label, otherwise `](` is incidental text.
  return block.lastIndexOf("[", linkUrlStart) !== -1;
}

/**
 * Uses `marked.lexer()` to split a markdown string into block-level tokens
 * (paragraphs, headings, code fences, lists, etc.) and returns their raw strings.
 *
 * This is the key to block-level memoization: when a new streaming token arrives,
 * the full content is re-tokenized, but completed blocks produce the exact same
 * raw string as the previous render. `MemoizedMarkdownBlock` uses a string equality
 * check to skip re-rendering those stable blocks.
 *
 * Note: the lexer itself is O(n) per call (it runs on the full string every time).
 * The win is in avoiding O(n) *rendering* for stable blocks — only the last
 * (actively streaming) block's React tree is rebuilt.
 *
 * The final block is withheld while it ends in an unterminated link, so a long
 * URL appears as a finished link rather than streaming in as raw text.
 */
export function parseMarkdownIntoBlocks(markdown: string): string[] {
  const tokens = marked.lexer(markdown);
  const blocks = tokens.map((token) => token.raw).filter((block) => block.trim().length > 0);

  // Only the final block can be mid-stream; earlier ones are already settled.
  const lastBlock = blocks[blocks.length - 1];
  if (lastBlock !== undefined && hasUnclosedTrailingLink(lastBlock)) {
    return blocks.slice(0, -1);
  }
  return blocks;
}
